import { defineStore } from "pinia";
import { computed } from "vue";
import { useAppsStore } from "./appsStore";
import { useDataSourcesStore } from "@/pinia/dataSourcesStore";
import { useCustomFieldsStore } from "@/pinia/customFieldsStore";
import { customRef } from "vue";
import { parseLiquidTemplateSync } from "@/utils/liquidParser";
import { resolveInjects } from "@/utils/injectParser";

export const useLiquidContextStore = defineStore("liquid-context", () => {
  const dataSourcesStore = useDataSourcesStore();
  const appsStore = useAppsStore();
  const customFieldsStore = useCustomFieldsStore();
  let customFieldsLoadedForAppId = null;
  const previewUserContext = customRef((track, trigger) => {
    let value = {};

    return {
      get() {
        track();
        return value;
      },
      set(newValue) {
        value = newValue || {};
        trigger();
      },
    };
  });

  const context = computed(() => ({
    user: previewUserContext.value || {},
    ...(dataSourcesStore.dataSourcesToObject || {}),
  }));

  const isLiquidPushEnabled = computed(() => appsStore.app?.flags?.liquid_push);

  const bracketEnabled = computed(
    () => appsStore.app?.flags?.disable_bracket_interpolation !== true,
  );

  const traitTypes = computed(() =>
    Object.fromEntries(
      (customFieldsStore.segmentCustomFields || []).map(({ label, data_type }) => [
        label,
        data_type,
      ]),
    ),
  );

  const liquidify = (template, push = false) => {
    if (push && !isLiquidPushEnabled.value) return template;
    const rendered = parseLiquidTemplateSync(template, context.value, { enabled: true });
    if (!bracketEnabled.value) return rendered;
    return resolveInjects(rendered, {
      user: previewUserContext.value || {},
      dataSources: dataSourcesStore.dataSourcesToObject || {},
      traitTypes: traitTypes.value,
    });
  };

  const init = () => {
    // Let dataSourcesStore handle deduplication
    dataSourcesStore.getDataSources(appsStore.app.id);
    // segmentCustomFields may hold another app's fields (loaded via useCustomFields),
    // so skip only when this store already fetched for the current app
    if (customFieldsLoadedForAppId !== appsStore.app.id) {
      customFieldsLoadedForAppId = appsStore.app.id;
      customFieldsStore.getAppCustomFields({ appId: appsStore.app.id, fieldType: "custom" });
    }
  };

  return {
    context,
    previewUserContext,
    liquidify,
    isLiquidPushEnabled,
    bracketEnabled,
    traitTypes,
    init,
  };
});
